import React from 'react';
import { useTranslation } from 'next-i18next';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import PersonalizedHeader from '@components/personalized/personalized-header/personalized-header';
import PersonalizedSort from '@components/personalized/personalized-sort/personalized-sort';
import { useQuery } from '@tanstack/react-query';
import { UserProfile } from '@interfaces/models/userProfile';
import { ProfileQueryKeys } from '@enums/react-query-keys';
import { getUserProfile } from '@api/profile';
import useUser from '@hooks/user/use-user';
import { useRouter } from 'next/router';
import { PageType } from '@interfaces/common/common';
import { capitalize } from '@helpers/utils/general';

type FavoritesHeaderProps = {
  handleSortClick: (val: string) => void;
  sortBy: string;
  totalCount: number;
  isLoadingProducts: boolean;
  hasHeader?: boolean;
  hasOnlySubheader?: boolean;
  pageType: PageType;
};

const FAV_SORT_OPTIONS = {
  dateDesc: 'FAVORITE_PAGE.SORT.RECENTLY_ADDED',
  recentPriceDrop: 'FAVORITE_PAGE.SORT.PRICE_DROPS',
  price: 'FAVORITE_PAGE.SORT.PRICE_LOW_TO_HIGH',
  priceDesc: 'FAVORITE_PAGE.SORT.PRICE_HIGH_TO_LOW',
};

const FavoritesHeader: React.FC<FavoritesHeaderProps> = ({
  totalCount,
  sortBy,
  handleSortClick,
  isLoadingProducts,
  hasHeader,
  hasOnlySubheader,
  pageType,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const router = useRouter();
  const { favoritesUserSlug } = router?.query as {
    favoritesUserSlug?: string;
  };

  const { data: profileUser } = useQuery<UserProfile | null>({
    queryKey: [ProfileQueryKeys.PROFILE_USER],
    queryFn: () => getUserProfile(favoritesUserSlug?.[0]),
    staleTime: Infinity,
    enabled: favoritesUserSlug?.[0] !== user?.id,
  });

  return (
    <ConditionalWrapper
      condition={hasHeader || hasOnlySubheader}
      wrap={(children) => (
        <PersonalizedHeader
          productsCount={totalCount}
          isLoading={isLoadingProducts}
          title={
            favoritesUserSlug?.[0] !== user?.id && pageType === 'favourite'
              ? profileUser?.firstname || profileUser?.username
                ? t('FAVORITES.TITLE.TEXT', {
                    username: capitalize(profileUser?.firstname || profileUser?.username),
                  })
                : t('FAVORITE_PAGE.TEXT')
              : t('FAVORITE_PAGE.TEXT')
          }
          subTitle={totalCount === 1 ? t('RECOMMENDED_PRODUCT.ITEM_TEXT') : t('FAVORITE_PAGE.ITEMS_TEXT')}
          pageType={pageType}
          hasOnlySubheader={hasOnlySubheader}
        >
          {children}
        </PersonalizedHeader>
      )}
    >
      {(hasHeader || hasOnlySubheader) && (
        <PersonalizedSort
          disabled={!totalCount}
          options={FAV_SORT_OPTIONS}
          value={sortBy}
          onChange={(sortedValue) => handleSortClick(sortedValue)}
          pageType={pageType}
          isInsideTheFeed={!hasHeader}
        />
      )}
    </ConditionalWrapper>
  );
};

export default FavoritesHeader;
